import { Fragment, useEffect, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { StatusPill } from '@/components/StatusPill';
import { Pager } from '@/components/Pager';
import { statusPillClass } from '@/components/status';
import type {
  KtInsightsExport,
  KtInsightsExportStatusCount,
  SubscriptionRunStatus
} from '@/types';
import { HelpLink } from '@/components/HelpLink';

const pageSize = 50;

/**
 * MANDW01's own status wording, mapped onto the portal's palette so a status keeps one
 * colour across the app. 'Created' is shown in the Stale colour deliberately: in this
 * table it means the PBRS config was built and nothing ever executed it, which is normal
 * for a minute or two and permanent after that. Per-row verdicts are still exact — a
 * genuinely fresh one reads as Pending.
 */
function chipStatus(raw: string): SubscriptionRunStatus {
  const value = raw.toLowerCase();
  if (value.includes('fail') || value.includes('error')) return 'Failed';
  if (value.includes('success')) return 'Success';
  if (value === 'created') return 'Stale';
  return 'Unknown';
}

export function KtInsightsHistory() {
  const api = useApi();
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [skip, setSkip] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Any change to the filters invalidates the current offset — page 4 of the old result
  // set is meaningless against the new one.
  useEffect(() => setSkip(0), [status, debouncedSearch, from, to]);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['ktinsights-export-history', status, debouncedSearch, from, to, skip],
    queryFn: () => api.ktInsightsExportHistory({
      status: status || undefined,
      search: debouncedSearch || undefined,
      // A date input is a calendar day; the API takes instants.
      from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
      to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
      skip,
      take: pageSize
    }),
    // Without this the table empties on every page step and the layout jumps.
    placeholderData: keepPreviousData,
    refetchInterval: 60_000
  });

  if (error) return <div className="error-banner">{(error as Error).message}</div>;

  const exports = data?.exports ?? [];
  const total = data?.total ?? 0;
  const counts = data?.statusCounts ?? [];
  const unconfigured = data?.source === 'unconfigured';

  return (
    <>
      <div className="page-header">
        <div>
          <h1>KT Insights export history</h1>
          <div className="sub">
            Every KT Insights export on the report server and what became of it — whoever triggered it,
            not just the ones scheduled here.
            {data?.scopedToRecipient && ' Showing exports addressed to you.'}
            {' '}<HelpLink slug="check-a-kt-insights-export" />
          </div>
        </div>
      </div>

      {unconfigured && (
        <div className="error-banner" style={{ marginBottom: 14 }}>
          KT Insights export history is not available on this environment — the portal does not hold a
          read grant on the export database.
        </div>
      )}

      {data?.source === 'sample' && (
        <div className="placeholder" style={{ marginBottom: 14 }}>
          Sample data — the report server is not reachable from here.
        </div>
      )}

      <StuckBanner counts={counts} />

      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <FilterLabel>From</FilterLabel>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div>
          <FilterLabel>To</FilterLabel>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <div style={{ flex: '1 1 260px', minWidth: 220 }}>
          <FilterLabel>
            {data?.scopedToRecipient ? 'Search your exports' : 'Recipient or export name'}
          </FilterLabel>
          <input
            type="search"
            placeholder={data?.scopedToRecipient ? 'Search…' : 'e.g. someone@customer.org'}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        {(from || to || search || status) && (
          <button
            className="btn-ghost btn-sm"
            onClick={() => { setStatus(''); setSearch(''); setFrom(''); setTo(''); }}
          >
            Clear filters
          </button>
        )}
      </div>

      <StatusChips counts={counts} total={total} selected={status} onSelect={setStatus} />

      <div className="card" style={{ marginTop: 12 }}>
        {isLoading ? <div className="placeholder">Loading…</div> : (
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 160 }}>Requested</th>
                <th>Export</th>
                <th>Delivered to</th>
                <th style={{ width: 90 }}>Took</th>
                <th style={{ width: 110 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {exports.length === 0 ? (
                <tr>
                  <td colSpan={5} className="placeholder">
                    {unconfigured ? 'Not configured.' : 'No exports match these filters.'}
                  </td>
                </tr>
              ) : exports.map(x => (
                <Fragment key={x.exportId}>
                  <ExportRow
                    export={x}
                    expanded={expanded === x.exportId}
                    onToggle={() => setExpanded(expanded === x.exportId ? null : x.exportId)}
                  />
                  {expanded === x.exportId && (
                    <tr className="run-detail-row">
                      <td colSpan={5}><ExportDetail export={x} /></td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pager
        skip={skip}
        take={pageSize}
        shown={exports.length}
        total={total}
        busy={isFetching}
        onChange={setSkip}
      />
    </>
  );
}

function FilterLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: '.78rem', color: 'var(--kt-grey-500)', marginBottom: 4 }}>{children}</div>
  );
}

/**
 * The headline the page exists for. An export left at 'Created' never started, and nothing in
 * the pipeline revisits it — so this is a backlog that will sit there until someone acts.
 */
function StuckBanner({ counts }: { counts: KtInsightsExportStatusCount[] }) {
  const created = counts.find(c => c.status.toLowerCase() === 'created');
  if (!created || created.neverStarted === 0) return null;

  return (
    <div
      className="error-banner"
      style={{ marginBottom: 14, background: 'var(--kt-orange-bg)', borderColor: 'var(--kt-orange-border)', color: 'var(--kt-grey-700)' }}
    >
      <strong style={{ color: 'var(--kt-orange)' }}>
        {created.neverStarted} export{created.neverStarted === 1 ? '' : 's'} never started.
      </strong>{' '}
      A healthy export begins within a minute or two of being queued, so anything still sitting at
      “Created” will not complete on its own.
      {created.latestRequestedUtc && (
        <> Most recent: {new Date(created.latestRequestedUtc).toLocaleString()}.</>
      )}
    </div>
  );
}

function StatusChips({ counts, total, selected, onSelect }: {
  counts: KtInsightsExportStatusCount[];
  total: number;
  selected: string;
  onSelect: (status: string) => void;
}) {
  if (counts.length === 0) return null;

  // The roll-up ignores the status filter, so its sum is the unfiltered total — which is what
  // the All chip should read, not the current page's filtered count.
  const all = counts.reduce((sum, c) => sum + c.total, 0);

  return (
    <div className="status-filters" role="group" aria-label="Filter by export status">
      <button
        type="button"
        className={`pill status-filter status-filter-all${selected === '' ? ' on' : ''}`}
        aria-pressed={selected === ''}
        onClick={() => onSelect('')}
      >
        All<span className="count">{selected === '' ? total : all}</span>
      </button>
      {counts.map(c => (
        <button
          key={c.status}
          type="button"
          className={`pill status-filter ${statusPillClass(chipStatus(c.status))}${selected === c.status ? ' on' : ''}`}
          aria-pressed={selected === c.status}
          onClick={() => onSelect(selected === c.status ? '' : c.status)}
          title={c.neverStarted > 0 ? `${c.neverStarted} of these never started` : undefined}
        >
          {c.status}<span className="count">{c.total}</span>
        </button>
      ))}
    </div>
  );
}

function ExportRow({ export: x, expanded, onToggle }: {
  export: KtInsightsExport;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <tr onClick={onToggle} aria-expanded={expanded}>
      <td data-label="Requested" style={{ whiteSpace: 'nowrap', fontSize: '.85rem' }}>
        {x.requestedUtc ? new Date(x.requestedUtc).toLocaleString() : '—'}
      </td>
      <td data-label="">
        <div className="report-name">{x.reportName ?? x.exportName}</div>
        <div className="report-path">
          #{x.exportId}
          {x.exportType ? ` · ${x.exportType}` : ''}
          {x.pageList ? ` · pages ${truncate(x.pageList, 40)}` : ''}
        </div>
        {x.statusDetail && (
          <div style={{ fontSize: '.78rem', color: 'var(--kt-red)', marginTop: 3 }}>{x.statusDetail}</div>
        )}
      </td>
      <td data-label="Delivered to" style={{ fontSize: '.85rem' }}>
        {x.recipient ?? '—'}
        {x.filterSummary && (
          <div style={{ fontSize: '.75rem', color: 'var(--kt-grey-400)', marginTop: 2 }}>
            {truncate(x.filterSummary, 70)}
          </div>
        )}
      </td>
      <td data-label="Took" style={{ whiteSpace: 'nowrap', fontSize: '.85rem' }}>{duration(x)}</td>
      <td data-label="Status"><StatusPill status={x.outcome} /></td>
    </tr>
  );
}

/**
 * The semicolons in `pageList` split it into the export's RENDER GROUPS, not into reports.
 *
 * Verified against PBRS.ExportReport on MANDW01, 2026-08-05. PBRS.ExportRequest walks the pages a
 * request resolved to and starts a new ExportReport row wherever DefaultWidth/DefaultHeight change,
 * because each row carries the PageWidth/PageHeight the renderer uses; the history query then joins
 * those rows with '; '. Export 7490's four groups are exactly the four page sizes report 274's
 * pages come in.
 *
 * So `1,2,3,4,5,6,7,8,9; 10; 11,13,15; 17` is one report in four canvas sizes, and rendering it as
 * one flat list would lose a real distinction while rendering it as "four reports" would invent one.
 */
function pageGroups(pageList: string | null | undefined) {
  return (pageList ?? '').split(';').map(g => g.trim()).filter(Boolean);
}

function ExportDetail({ export: x }: { export: KtInsightsExport }) {
  const filters = (x.filterSummary ?? '').split('; ').filter(Boolean);

  return (
    <div className="run-detail">
      <div className="run-detail-title">Export {x.exportId}</div>
      <dl className="run-params">
        <dt>Report Server status</dt>
        <dd><span className="run-param-value">{x.exportStatus ?? '(none)'}</span></dd>

        <dt>Queued</dt>
        <dd><span className="run-param-value">{x.requestedUtc ? new Date(x.requestedUtc).toLocaleString() : '—'}</span></dd>

        <dt>Started</dt>
        <dd>
          {x.startedUtc
            ? <span className="run-param-value">{new Date(x.startedUtc).toLocaleString()}</span>
            : <em className="run-param-blank">never started</em>}
        </dd>

        <dt>Finished</dt>
        <dd>
          {x.finishedUtc
            ? <span className="run-param-value">{new Date(x.finishedUtc).toLocaleString()}</span>
            : <em className="run-param-blank">—</em>}
        </dd>

        {x.cc && (<><dt>CC</dt><dd><span className="run-param-value">{x.cc}</span></dd></>)}

        {x.userCheckExportId != null && (
          <>
            <dt>User check</dt>
            <dd>
              <span className="run-param-value">
                export {x.userCheckExportId} — {x.userCheckStatus ?? 'unknown'}
              </span>
            </dd>
          </>
        )}

        {x.pageList && (
          <>
            <dt>Pages</dt>
            <dd>
              {pageGroups(x.pageList).map((group, i) => (
                <span key={i} className="run-param-value">{group}</span>
              ))}
            </dd>
          </>
        )}

        {filters.length > 0 && (
          <>
            <dt>Filters</dt>
            <dd>{filters.map((f, i) => <span key={i} className="run-param-value">{f}</span>)}</dd>
          </>
        )}
      </dl>
      {pageGroups(x.pageList).length > 1 && (
        <div className="run-detail-note" style={{ color: 'var(--kt-grey-500)' }}>
          The pages are shown in the groups PBRS rendered them in — one per page size. Each group is
          a row in <code>PBRS.ExportReport</code> carrying its own PageWidth/PageHeight, and
          PBRS.ExportRequest starts a new one wherever the next page's canvas differs from the last.
          Export 7490 splits as 1568×900 for pages 1-9, 1150×1000 for page 10, 1568×1402 for 11, 13
          and 15, and so on — so a group boundary is a change of page size, not a change of report.
          Gaps within a group are pages this recipient did not ask for or is not entitled to.
        </div>
      )}
      {filters.length === 0 && (
        <div className="run-detail-note">This export recorded no report filters.</div>
      )}
    </div>
  );
}


function duration({ startedUtc, finishedUtc }: KtInsightsExport) {
  if (!startedUtc || !finishedUtc) return '—';
  const seconds = (new Date(finishedUtc).getTime() - new Date(startedUtc).getTime()) / 1000;
  if (seconds < 0) return '—';
  return seconds < 90 ? `${seconds.toFixed(0)}s` : `${(seconds / 60).toFixed(1)}m`;
}

function truncate(value: string, max: number) {
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}
